import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaListUl } from 'react-icons/fa';
import { FaMapLocationDot } from 'react-icons/fa6';
import { trackWindowScroll } from 'react-lazy-load-image-component';
import { useDispatch, useSelector } from 'react-redux';
import { useDebounceCallback } from 'usehooks-ts';

import { isNil } from 'lodash-es';
import { ROUTES } from '../../../constants/Constants';
import useAppNavigation from '../../../hooks/useAppNavigation';
import useMapView from '../../../hooks/useMapView';
import useTimezone from '../../../hooks/useTimezone';
import type {
  PartnerResultDescriptorType,
  SearchPartnersRequestType,
} from '../../../redux/ducks/SearchDucks.types';
import { setSearchSelection } from '../../../redux/ducks/StorageDucks';
import type { SearchSelectionType } from '../../../redux/ducks/StorageDucks.types';
import {
  setCurrentlySelectedMapItem,
  setListMapItems,
} from '../../../redux/ducks/UIDucks';
import type { RootReducerType } from '../../../redux/reducers/RootReducer.types';
import Spinner from '../../layout/Spinner/Spinner';
import WelcomeListView from './WelcomeListView';
import WelcomeMapView from './WelcomeMapView';

export const convertToBoundsString = (mapBounds: number[]): string =>
  `${mapBounds[0]},${mapBounds[1]},${mapBounds[2]},${mapBounds[3]}`;

export const convertToSearchType = (
  input: SearchSelectionType,
  offset?: number,
): SearchPartnersRequestType => {
  const data: SearchPartnersRequestType = {
    category: input?.category,
    latitude:
      input?.placeLocation?.latlng && input?.placeLocation?.latlng.length === 2
        ? input?.placeLocation?.latlng[0]
        : undefined,
    longitude:
      input?.placeLocation?.latlng && input?.placeLocation?.latlng.length === 2
        ? input?.placeLocation?.latlng[1]
        : undefined,
    timezone: input.time.timezone,
  };

  if (
    (input.time?.startTime && !input.time?.endTime) ||
    (input.time?.endTime && input.time.endTime > Date.now())
  ) {
    data.startTime = input.time?.startTime || undefined;
    data.endTime = input.time?.endTime || undefined;
  }

  if (offset) {
    data.offset = offset;
  }

  if (input.bounds) {
    const { bounds } = input;

    data.nelat = bounds[0];
    data.nelng = bounds[1];
    data.swlat = bounds[2];
    data.swlng = bounds[3];

    data.latitude = (data.nelat + data.swlat) * 0.5;
    data.longitude = (data.nelng + data.swlng) * 0.5;
  }

  return data;
};

const WelcomeBody = () => {
  const { currentTimezone } = useTimezone();
  const dispatch = useDispatch();
  const mapView = useMapView();
  const { navigate, toLocalizedRoute } = useAppNavigation();
  const { t } = useTranslation();
  const [selectedPartner, setSelectedPartner] = useState<
    PartnerResultDescriptorType | undefined
  >();
  const [visibleItems, setVisibleItems] = useState<
    PartnerResultDescriptorType[]
  >([]);
  const isSearchingPartners = useSelector(
    (state: RootReducerType) => state.search.isSearchingPartners,
  );
  const searchSelection = useSelector(
    (state: RootReducerType) => state.storage.searchSelection,
  );
  const searchPartnersResponse = useSelector(
    (state: RootReducerType) => state.search.searchPartnersResponse,
  );
  const isOnPwa = useSelector((state: RootReducerType) => state.ui.isOnPwa);

  const switchView = () => {
    if (mapView.isOpen) {
      dispatch(setCurrentlySelectedMapItem(undefined));
      if (!isNil(searchSelection)) {
        dispatch(
          setSearchSelection({
            ...searchSelection,
            bounds: undefined,
          }),
        );
      } else {
        dispatch(
          setSearchSelection({
            time: {
              timezone: currentTimezone,
            },
          }),
        );
      }
      mapView.onClose();
    } else {
      dispatch(setListMapItems(visibleItems));
      mapView.onOpen();
    }
  };

  const itemVisibilityChanged = ({
    partnerDescriptor,
    isVisible,
  }: {
    partnerDescriptor: PartnerResultDescriptorType;
    isVisible: boolean;
  }) => {
    if (isVisible === true) {
      setVisibleItems((existingItems) => [
        ...existingItems.filter((i) => i.id !== partnerDescriptor.id),
        { ...partnerDescriptor },
      ]);
    } else {
      setVisibleItems((existingItems) =>
        [...existingItems].filter((i) => i.id !== partnerDescriptor.id),
      );
    }
  };

  const onPartnerSelected = (partner: PartnerResultDescriptorType) => {
    setSelectedPartner(partner);
  };

  const delayedWindowOpen = useDebounceCallback((url) => {
    if (typeof window !== 'undefined') {
      window.open(url);
    }
  }, 100);

  useEffect(() => {
    if (!isNil(selectedPartner)) {
      const route = toLocalizedRoute(
        ROUTES.PARTNER_PROFILE_ROUTE.replace(
          ':partnerPath',
          `${selectedPartner.path}`,
        ),
      );

      if (isOnPwa) {
        navigate(route);
      } else {
        delayedWindowOpen(route);
      }
    }
  }, [selectedPartner]);

  useEffect(() => {
    if (searchPartnersResponse?.data && searchSelection?.time?.startTime) {
      const timeChanged =
        searchSelection.time.startTime !==
          searchPartnersResponse?.data.startTime ||
        (searchSelection.time?.endTime &&
          searchSelection.time.endTime !==
            searchPartnersResponse?.data.endTime);
      if (timeChanged) {
        dispatch(
          setSearchSelection({
            ...searchSelection,
            time: {
              startTime: searchPartnersResponse?.data.startTime,
              endTime: searchPartnersResponse?.data.endTime,
              timezone: currentTimezone,
            },
          }),
        );
      }
    }
  }, [searchPartnersResponse]);

  useEffect(() => {
    if (
      !mapView.isOpen &&
      !isNil(searchSelection) &&
      !isNil(searchSelection?.bounds)
    ) {
      dispatch(
        setSearchSelection({
          ...searchSelection,
          bounds: undefined,
        }),
      );
    }
  }, [mapView]);

  useEffect(() => {
    if (isNil(searchSelection)) {
      dispatch(
        setSearchSelection({
          time: {
            timezone: currentTimezone,
          },
        }),
      );
    }
  }, [searchSelection, currentTimezone]);

  return (
    <div className="flex min-w-full grow flex-col items-center justify-center">
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-20 min-w-full grow bg-transparent">
        <div className="mb-4 flex min-h-full min-w-full flex-col items-center justify-end md:mb-11">
          <button
            id={
              mapView.isOpen
                ? 'welcome_page_show_list_button'
                : 'welcome_page_show_map_button'
            }
            key={
              mapView.isOpen
                ? 'welcome_page_show_list_button'
                : 'welcome_page_show_map_button'
            }
            aria-label={
              mapView.isOpen
                ? t('static.generic.show_list')
                : t('static.generic.show_map')
            }
            data-test-id={
              mapView.isOpen
                ? 'welcome_page_show_list_button'
                : 'welcome_page_show_map_button'
            }
            data-umami-event={
              mapView.isOpen
                ? 'welcome_page_show_list_button'
                : 'welcome_page_show_map_button'
            }
            className="pointer-events-auto flex scale-75 cursor-pointer flex-col items-center justify-center rounded-full bg-slate-800 px-6 py-4 text-center text-white shadow-md hover:bg-sky-600 hover:shadow-lg md:scale-100 md:px-4 md:py-3"
            onClick={switchView}
            type="button"
          >
            <span className="flex h-full flex-row items-start justify-between gap-2">
              {mapView.isOpen ? (
                !isSearchingPartners ? (
                  <>
                    <span className="hidden min-h-full flex-col items-center justify-center font-bold md:flex">
                      {t('static.generic.show_list')}
                    </span>
                    <span className="flex min-h-full flex-col items-center justify-center font-bold md:hidden">
                      {t('static.generic.show_list_short')}
                    </span>
                    <span className="flex min-h-full flex-col items-end justify-end pt-[2px] font-bold">
                      <FaListUl size={20} />
                    </span>
                  </>
                ) : (
                  <span>
                    <Spinner color="fill-black" />
                  </span>
                )
              ) : (
                <>
                  <span className="hidden min-h-full flex-col items-center justify-center font-bold md:flex">
                    {t('static.generic.show_map')}
                  </span>
                  <span className="flex min-h-full flex-col items-center justify-center font-bold md:hidden">
                    {t('static.generic.show_map_short')}
                  </span>
                  <span className="flex min-h-full flex-col items-end justify-end font-bold">
                    <FaMapLocationDot size={22} />
                  </span>
                </>
              )}
            </span>
          </button>
        </div>
      </div>
      {mapView.isOpen ? (
        <div className="fixed top-[80px]">
          <WelcomeMapView onPartnerSelected={onPartnerSelected} />
        </div>
      ) : (
        <div
          className="size-full max-w-screen-2xl grow"
          id="scrollablePartnersList"
        >
          <WelcomeListView
            onPartnerSelected={onPartnerSelected}
            onPartnerVisibilityChange={itemVisibilityChanged}
            scrollableTarget="scrollablePartnersList"
          />
        </div>
      )}
    </div>
  );
};

export default trackWindowScroll(WelcomeBody);
